import { Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui'
import { useClients } from '@/hooks'
import type { Client, ClientType } from '@/api'

const clientTypeLabels: Record<ClientType, string> = {
  individual: 'Individual',
  company: 'Company',
  llp: 'LLP',
  partnership: 'Partnership',
}

function ClientRow({ client }: { client: Client }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium truncate">{client.name}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
            {clientTypeLabels[client.client_type]}
          </span>
          {!client.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
              Inactive
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1">
          {client.email && (
            <span className="text-xs text-muted-foreground truncate">
              {client.email}
            </span>
          )}
          {client.phone && (
            <span className="text-xs text-muted-foreground">
              {client.phone}
            </span>
          )}
        </div>
      </div>
      <div className="ml-4 shrink-0 text-right">
        {client.pan && (
          <span className="text-xs text-muted-foreground font-mono">
            PAN: {client.pan}
          </span>
        )}
        {client.gstin && (
          <div className="text-xs text-muted-foreground font-mono">
            GSTIN: {client.gstin}
          </div>
        )}
      </div>
    </div>
  )
}

function ClientSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between py-3 px-4 rounded-lg border">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-3 w-28 ml-4" />
        </div>
      ))}
    </div>
  )
}

export function Clients() {
  const { data, isLoading, isError, error } = useClients()

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
        <p className="text-muted-foreground">
          View and manage your firm's client directory.
        </p>
      </div>

      {/* Main content */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client Directory</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <ClientSkeleton />}

          {isError && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive">Failed to load clients</p>
              <p className="text-sm text-muted-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {(!data?.items || data.items.length === 0) ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No clients yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Clients will appear here once added to your firm.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.items.map((client) => (
                    <ClientRow key={client.id} client={client} />
                  ))}
                </div>
              )}

              {data && data.items.length > 0 && (
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Showing {data.items.length} of {data.total} clients
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
